/**
 * Telnyx `@telnyx/webrtc` device singleton — the Telnyx counterpart of the
 * Twilio module `voice-device.ts`, exposed through the same module-scope
 * function surface and consumed via the provider-neutral facade
 * `session-device.ts`.
 *
 * Telnyx model differences that shape this module (spike-validated 2026-07-12,
 * docs/telnyx-decision-log.md):
 *  - The browser registers as a per-agent telephony credential via a minted
 *    `login_token`; client lifecycle is `telnyx.ready` / `telnyx.error` /
 *    `telnyx.socket.close` plus per-call `telnyx.notification` callUpdates
 *    with states `new → ringing → answering → active` and `hangup → destroy`.
 *  - EVERY leg reaching this browser is an incoming INVITE dialed by our
 *    backend — including the agent leg of an OUTBOUND call (browser-originated
 *    dials never hit the Call Control app). `connectOutboundLeg` therefore
 *    does not dial: it auto-answers the expected backend-dialed leg.
 *  - Correlation: the backend stamps `X-callId` as a custom SIP header on
 *    agent dials. When absent (header passthrough is best-effort), unmatched
 *    legs park in arrival order and `acceptPending`/outbound expectation pop
 *    the oldest — exact for the single-caller case.
 */
import { TelnyxRTC } from '@telnyx/webrtc';
import { voiceApi } from '@/api/services/voice.api';
import type { IMintedVoiceToken } from '../types';
import type { IVoiceDeviceHandlers } from './types';
import { recoveryDelayMs } from './voice-device';

/** Structural slice of the SDK's call object we rely on (SDK types are loose). */
interface ITelnyxCall {
    readonly id: string;
    readonly state: string;
    readonly direction: string;
    readonly options?: {
        readonly customHeaders?: ReadonlyArray<{ name?: string; value?: string }>;
    };
    answer(): void;
    hangup(): void;
    muteAudio(): void;
    unmuteAudio(): void;
    dtmf(digit: string): void;
}

interface ITelnyxNotification {
    readonly type: string;
    readonly call?: ITelnyxCall;
}

const AUDIO_ELEMENT_ID = 'ezytext-telnyx-remote-audio';
const OUTBOUND_EXPECTATION_TTL_MS = 40_000;
const TOKEN_REFRESH_MARGIN_MS = 60_000;
/**
 * An answered leg must reach the SDK `active` state (media up) within this
 * window. A leg that answers on signaling but never activates is a silent
 * call: the backend conferences it (SIP 200 OK) while both sides hear
 * nothing, and the leg lingers as a zombie holding this agent's credential.
 */
const ANSWER_ACTIVATION_TIMEOUT_MS = 10_000;

let client: TelnyxRTC | null = null;
let handlers: IVoiceDeviceHandlers | null = null;
let activeLeg: { call: ITelnyxCall; callId: string } | null = null;
let activeMuted = false;
/** Ringing legs matched to an app callId (via X-callId header). */
const pendingIncoming = new Map<string, ITelnyxCall>();
/** Ringing legs with no correlating header, in arrival order. */
let unmatched: ITelnyxCall[] = [];
/** callId assigned to a leg when it was answered from the unmatched pool. */
const assignedCallIds = new WeakMap<object, string>();
/** Every leg this browser answered, until its hangup/destroy — lets the WS
 *  `ended` handler tear down legs the backend no longer controls. */
const answeredLegs = new Map<string, ITelnyxCall>();
/** Per-callId timers enforcing ANSWER_ACTIVATION_TIMEOUT_MS. */
const activationWatchdogs = new Map<string, ReturnType<typeof setTimeout>>();
let outboundExpectation: { callId: string; timer: ReturnType<typeof setTimeout> } | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let recoveryAttempt = 0;

/** SDK error payloads are plain objects (e.g. `{error: {code, message}}`) —
 *  `String()` would flatten them to "[object Object]" and destroy the one
 *  diagnostic we get from the gateway. */
function describeError(error: unknown): string {
    if (typeof error === 'string') return error;
    try {
        return JSON.stringify(error);
    } catch {
        return String(error);
    }
}

function headerCallId(call: ITelnyxCall): string | null {
    const header = call.options?.customHeaders?.find(
        (h) => (h.name ?? '').toLowerCase() === 'x-callid',
    );
    return header?.value ?? null;
}

function callIdOf(call: ITelnyxCall): string | null {
    return headerCallId(call) ?? assignedCallIds.get(call) ?? null;
}

function ensureAudioElement(): void {
    if (document.getElementById(AUDIO_ELEMENT_ID)) return;
    const el = document.createElement('audio');
    el.id = AUDIO_ELEMENT_ID;
    el.autoplay = true;
    document.body.appendChild(el);
}

function clearOutboundExpectation(): void {
    if (outboundExpectation) {
        clearTimeout(outboundExpectation.timer);
        outboundExpectation = null;
    }
}

function cancelTimers(): void {
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
    if (recoveryTimer) {
        clearTimeout(recoveryTimer);
        recoveryTimer = null;
    }
}

/** One pending re-init at a time; each failure schedules the next attempt. */
function scheduleRecovery(): void {
    if (recoveryTimer || !handlers) return;
    const delay = recoveryDelayMs(recoveryAttempt);
    recoveryAttempt += 1;
    recoveryTimer = setTimeout(() => {
        recoveryTimer = null;
        void reinit();
    }, delay);
}

async function reinit(): Promise<void> {
    const current = handlers;
    if (!current) return; // destroyed while waiting
    try {
        const minted = await voiceApi.mintToken();
        await initTelnyxDevice(minted, current);
        recoveryAttempt = 0;
    } catch (error: unknown) {
        console.warn('[voice] telnyx device recovery attempt failed', error);
        scheduleRecovery();
    }
}

function clearActivationWatchdog(callId: string): void {
    const timer = activationWatchdogs.get(callId);
    if (timer !== undefined) {
        clearTimeout(timer);
        activationWatchdogs.delete(callId);
    }
}

/** Kill an answered-but-never-active leg loudly instead of leaving a silent
 *  zombie call (see ANSWER_ACTIVATION_TIMEOUT_MS). */
function startActivationWatchdog(call: ITelnyxCall, callId: string): void {
    clearActivationWatchdog(callId);
    const timer = setTimeout(() => {
        activationWatchdogs.delete(callId);
        if (activeLeg?.call === call) return; // media came up
        console.warn('[voice] telnyx leg answered but never became active — hanging up', {
            callId,
            state: call.state,
        });
        if (answeredLegs.get(callId) === call) answeredLegs.delete(callId);
        try {
            call.hangup();
        } catch {
            // Leg already torn down server-side.
        }
        handlers?.onError?.(
            new Error(`Call media failed to establish (leg stuck in "${call.state}")`),
        );
    }, ANSWER_ACTIVATION_TIMEOUT_MS);
    activationWatchdogs.set(callId, timer);
}

function answerLeg(call: ITelnyxCall, callId: string): void {
    assignedCallIds.set(call, callId);
    pendingIncoming.delete(callId);
    unmatched = unmatched.filter((c) => c !== call);
    answeredLegs.set(callId, call);
    startActivationWatchdog(call, callId);
    call.answer();
}

function onNotification(notification: ITelnyxNotification): void {
    if (notification.type !== 'callUpdate' || !notification.call || !handlers) return;
    const call = notification.call;
    // Full state trace — `answering`/`early`/`held` etc. flow through the
    // default branch, and a leg stalling between `ringing` and `active` is
    // exactly the signature of a media-dead (silent) call.
    console.debug('[voice] telnyx callUpdate', {
        legId: call.id,
        state: call.state,
        callId: callIdOf(call),
    });

    switch (call.state) {
        case 'ringing': {
            if (call.direction !== 'inbound') return;
            // Outbound flow: this ringing leg IS this agent's leg of the call
            // the backend just created — answer it silently.
            const headerId = headerCallId(call);
            if (outboundExpectation && (!headerId || headerId === outboundExpectation.callId)) {
                const expected = outboundExpectation.callId;
                clearOutboundExpectation();
                answerLeg(call, expected);
                return;
            }
            if (headerId) {
                pendingIncoming.set(headerId, call);
                handlers.onIncoming({ callId: headerId });
            } else {
                // No correlating header — park it; the WS ringing queue drives
                // the UI and acceptPending pops this pool oldest-first.
                unmatched.push(call);
            }
            return;
        }
        case 'active': {
            const callId = callIdOf(call);
            if (!callId) return;
            clearActivationWatchdog(callId);
            activeLeg = { call, callId };
            activeMuted = false;
            handlers.onCallEvent(callId, { type: 'accept' });
            return;
        }
        case 'hangup':
        case 'destroy': {
            const callId = callIdOf(call);
            unmatched = unmatched.filter((c) => c !== call);
            if (callId) {
                clearActivationWatchdog(callId);
                if (answeredLegs.get(callId) === call) answeredLegs.delete(callId);
                if (pendingIncoming.get(callId) === call) {
                    pendingIncoming.delete(callId);
                    handlers.onCallEvent(callId, { type: 'cancel' });
                } else if (activeLeg?.call === call) {
                    activeLeg = null;
                    activeMuted = false;
                    handlers.onCallEvent(callId, { type: 'disconnect' });
                }
            }
            return;
        }
        default:
            return;
    }
}

/** Connects + registers the Telnyx client with an already-minted token. */
export async function initTelnyxDevice(
    minted: IMintedVoiceToken,
    nextHandlers: IVoiceDeviceHandlers,
): Promise<void> {
    destroyTelnyxDevice();
    handlers = nextHandlers;
    ensureAudioElement();

    const rtc = new TelnyxRTC({
        login_token: minted.token,
        // Dev-only SDK-internal logging (verto messages, peer/ICE lifecycle) —
        // the diagnostic channel for media-dead legs.
        ...(import.meta.env.DEV ? { debug: true } : {}),
    });
    // The SDK accepts an element id for remote audio playback.
    (rtc as unknown as { remoteElement: string }).remoteElement = AUDIO_ELEMENT_ID;

    rtc.on('telnyx.ready', () => {
        recoveryAttempt = 0;
        nextHandlers.onRegistered?.();
    });
    rtc.on('telnyx.error', (error: unknown) => {
        nextHandlers.onError?.(error instanceof Error ? error : new Error(describeError(error)));
    });
    rtc.on('telnyx.socket.close', () => {
        nextHandlers.onUnregistered?.();
        // Token expiry / transport loss — re-mint and reconnect, mirroring the
        // Twilio module's self-healing contract. destroy() clears `handlers`
        // first, so teardown never schedules this.
        if (client === rtc) scheduleRecovery();
    });
    rtc.on('telnyx.notification', onNotification);

    client = rtc;
    await rtc.connect();

    // Proactive re-login before the login_token expires.
    const expiresIn = Date.parse(minted.expiresAt) - Date.now() - TOKEN_REFRESH_MARGIN_MS;
    if (Number.isFinite(expiresIn) && expiresIn > 0) {
        refreshTimer = setTimeout(() => void reinit(), expiresIn);
    }
}

/**
 * Outbound (and held-call pick): the backend dials this agent's credential;
 * auto-answer that leg when it arrives. A leg that raced ahead of this call
 * is already parked in `unmatched` — answer it immediately.
 */
export function expectAndAnswerOutboundLeg(callId: string): void {
    const parked =
        unmatched.find((c) => headerCallId(c) === callId) ??
        (unmatched.length > 0 ? unmatched[0] : undefined);
    if (parked) {
        answerLeg(parked, callId);
        return;
    }
    const already = pendingIncoming.get(callId);
    if (already) {
        answerLeg(already, callId);
        return;
    }
    clearOutboundExpectation();
    outboundExpectation = {
        callId,
        timer: setTimeout(clearOutboundExpectation, OUTBOUND_EXPECTATION_TTL_MS),
    };
}

/**
 * Drop the auto-answer expectation for an outbound call that ended (failed
 * dial, caller hung up first, backend timeout). Without this, a stale
 * expectation inside its 40s TTL would silently auto-answer the NEXT
 * header-less inbound agent leg as if it were the outbound one.
 */
export function cancelOutboundExpectation(callId: string): void {
    if (outboundExpectation?.callId === callId) clearOutboundExpectation();
}

export function telnyxAcceptPending(callId: string): boolean {
    if (activeLeg) return false;
    const call = pendingIncoming.get(callId) ?? unmatched.shift();
    if (!call) return false;
    answerLeg(call, callId);
    return true;
}

export function telnyxRejectPending(callId: string): void {
    const call = pendingIncoming.get(callId) ?? unmatched.shift();
    if (!call) return;
    pendingIncoming.delete(callId);
    unmatched = unmatched.filter((c) => c !== call);
    call.hangup();
}

export function telnyxListPendingCallIds(): string[] {
    return [...pendingIncoming.keys()];
}

export function telnyxGetActiveCallId(): string | null {
    return activeLeg?.callId ?? null;
}

export function telnyxHangupActive(): void {
    activeLeg?.call.hangup();
}

/**
 * The backend says this call is over (WS `ended`) — tear down any leg this
 * browser still holds for it. Without this, a far-end-first hangup leaves the
 * agent's answered leg alive indefinitely (observed live: zombie legs holding
 * the SIP credential for 20+ minutes, blocking every later inbound ring).
 */
export function telnyxHangupLeg(callId: string): void {
    clearActivationWatchdog(callId);
    const leg = answeredLegs.get(callId);
    if (!leg) return;
    answeredLegs.delete(callId);
    try {
        leg.hangup();
    } catch {
        // Leg already torn down server-side.
    }
}

/** True when the SDK leg was acted on; false = no tracked active leg. */
export function telnyxSetMuted(muted: boolean): boolean {
    if (!activeLeg || !handlers) return false;
    // Mute-effectiveness trace: if the caller still hears the agent after a
    // mute, compare this legId against the `telnyx callUpdate … state:
    // 'active'` line — a mismatch means we muted a stale leg.
    console.debug('[voice] telnyx mute', {
        legId: activeLeg.call.id,
        callId: activeLeg.callId,
        muted,
    });
    if (muted) activeLeg.call.muteAudio();
    else activeLeg.call.unmuteAudio();
    activeMuted = muted;
    // The SDK has no dedicated mute notification — emit the neutral event here.
    handlers.onCallEvent(activeLeg.callId, { type: 'mute', muted });
    return true;
}

export function telnyxIsActiveMuted(): boolean {
    return activeMuted;
}

export function telnyxSendDigits(digits: string): void {
    if (!activeLeg) return;
    for (const digit of digits) activeLeg.call.dtmf(digit);
}

export function destroyTelnyxDevice(): void {
    handlers = null;
    activeLeg = null;
    activeMuted = false;
    pendingIncoming.clear();
    unmatched = [];
    answeredLegs.clear();
    for (const timer of activationWatchdogs.values()) clearTimeout(timer);
    activationWatchdogs.clear();
    clearOutboundExpectation();
    cancelTimers();
    recoveryAttempt = 0;
    if (!client) return;
    const dying = client;
    client = null;
    try {
        dying.disconnect();
    } catch {
        // Socket already gone — nothing to tear down.
    }
}
